// AGC: CONAB Price Table Component
// AGC: Admin panel component to query and display CONAB commodity price data
// AGC: Supports 4 datasets, 12 crops, state/city filtering
// AGC: Data source: CONAB (Companhia Nacional de Abastecimento - Brazilian government)

import React, { useState, useMemo } from 'react'
import { useConabPrices } from '../../hooks/useConabPrices'
import { useTranslation } from '../../hooks'
import { Database, Search, RefreshCw, Loader2, AlertCircle, ChevronDown } from 'lucide-react'
import type { ConabDataset, DroidfarmCrop, ConabQueryParams } from '../../services/conabService'

// AGC: Available datasets with labels
const DATASETS: { value: ConabDataset; label: string }[] = [
  { value: 'semanal_municipio', label: 'Semanal - Município' },
  { value: 'mensal_municipio', label: 'Mensal - Município' },
  { value: 'semanal_uf', label: 'Semanal - UF (Estado)' },
  { value: 'mensal_uf', label: 'Mensal - UF (Estado)' },
]

// AGC: Available crops with labels
const CROPS: { value: DroidfarmCrop; label: string }[] = [
  { value: 'soja', label: 'Soja' },
  { value: 'milho', label: 'Milho' },
  { value: 'cafe', label: 'Café' },
  { value: 'trigo', label: 'Trigo' },
  { value: 'arroz', label: 'Arroz' },
  { value: 'feijao', label: 'Feijão' },
  { value: 'cana', label: 'Cana' },
  { value: 'algodao', label: 'Algodão' },
  { value: 'laranja', label: 'Laranja' },
  { value: 'cacau', label: 'Cacau' },
  { value: 'batata', label: 'Batata' },
  { value: 'mandioca', label: 'Mandioca' },
]

// AGC: Brazilian state codes
const UF_LIST = [
  '', 'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ',
  'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]

/**
 * AGC: ConabPriceTable - Admin component for querying CONAB datasets
 * AGC: Features:
 * AGC: - Dataset selector (4 types)
 * AGC: - Crop dropdown (12 canonical crops)
 * AGC: - UF (state) filter
 * AGC: - Municipality filter (only for município datasets)
 * AGC: - Results table with date, price, location
 * AGC: - Cache metadata display
 */
export const ConabPriceTable: React.FC = () => {
  const { t } = useTranslation()

  // AGC: Filter state
  const [dataset, setDataset] = useState<ConabDataset>('semanal_municipio')
  const [product, setProduct] = useState<DroidfarmCrop>('soja')
  const [uf, setUf] = useState('')
  const [municipio, setMunicipio] = useState('')
  const [limit, setLimit] = useState(50)

  // AGC: Whether the user has clicked "Search" at least once
  const [hasSearched, setHasSearched] = useState(false)

  // AGC: Build query params (null = don't fetch yet)
  const queryParams = useMemo<ConabQueryParams | null>(() => {
    if (!hasSearched) return null
    return {
      dataset,
      product,
      uf: uf || undefined,
      municipio: municipio || undefined,
      limit,
    }
  }, [hasSearched, dataset, product, uf, municipio, limit])

  const { data, loading, error, refetch } = useConabPrices(queryParams)

  // AGC: Whether this is a município-level dataset
  const isMunicipioDataset = dataset.includes('municipio')

  // AGC: Handle search button click
  const handleSearch = () => {
    setHasSearched(true)
    // AGC: If already searched with same params, refetch
    if (hasSearched) {
      refetch()
    }
  }

  // AGC: Format price for display
  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return '—'
    return price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // AGC: Format date for display
  const formatDateStr = (date?: string): string => {
    if (!date) return '—'
    // AGC: If ISO format (yyyy-mm-dd), convert to dd/mm/yyyy
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`
    }
    return date
  }

  return (
    <div className="space-y-4">
      {/* AGC: Header */}
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-droid-600" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
          {t('conab.title') || 'CONAB - Preços Agropecuários'}
        </h2>
      </div>

      {/* AGC: Description */}
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('conab.description') || 'Dados oficiais do governo brasileiro (Companhia Nacional de Abastecimento). Preços semanais e mensais por município e UF.'}
      </p>

      {/* AGC: Filters */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg border border-slate-200 dark:border-white/5 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* AGC: Dataset selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              {t('conab.dataset') || 'Dataset'}
            </label>
            <div className="relative">
              <select
                value={dataset}
                onChange={(e) => setDataset(e.target.value as ConabDataset)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-admin-bg-dark text-slate-800 dark:text-slate-200 appearance-none pr-8"
              >
                {DATASETS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* AGC: Product selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              {t('conab.product') || 'Produto'}
            </label>
            <div className="relative">
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value as DroidfarmCrop)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-admin-bg-dark text-slate-800 dark:text-slate-200 appearance-none pr-8"
              >
                {CROPS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* AGC: UF (state) selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              {t('conab.uf') || 'UF (Estado)'}
            </label>
            <div className="relative">
              <select
                value={uf}
                onChange={(e) => setUf(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-admin-bg-dark text-slate-800 dark:text-slate-200 appearance-none pr-8"
              >
                <option value="">{t('conab.allStates') || 'Todos'}</option>
                {UF_LIST.filter(Boolean).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* AGC: Municipality input (only for município datasets) */}
          {isMunicipioDataset && (
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                {t('conab.municipio') || 'Município'}
              </label>
              <input
                type="text"
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                placeholder={t('conab.municipioPlaceholder') || 'Ex: Sorriso'}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-admin-bg-dark text-slate-800 dark:text-slate-200 placeholder-gray-400"
              />
            </div>
          )}

          {/* AGC: Limit selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              {t('conab.limit') || 'Limite'}
            </label>
            <div className="relative">
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-admin-bg-dark text-slate-800 dark:text-slate-200 appearance-none pr-8"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
              <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* AGC: Search button */}
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-droid-600 text-white hover:bg-droid-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {loading
                ? (t('conab.loading') || 'Carregando...')
                : (t('conab.search') || 'Buscar')
              }
            </button>
          </div>
        </div>
      </div>

      {/* AGC: Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* AGC: Results */}
      {data && !error && (
        <div className="bg-white dark:bg-admin-card-dark rounded-lg border border-slate-200 dark:border-white/5 overflow-hidden">
          {/* AGC: Meta info bar */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-white/[0.02] border-b border-slate-200 dark:border-white/5 text-xs text-slate-500 dark:text-slate-400">
            <span>
              <strong>{data.count}</strong> {t('conab.of') || 'de'} <strong>{data.totalMatches}</strong> {t('conab.results') || 'resultados'}
            </span>
            {data.meta?.lastFetchedAtISO && (
              <span>
                {t('conab.lastFetched') || 'Atualizado'}: {new Date(data.meta.lastFetchedAtISO).toLocaleString('pt-BR')}
              </span>
            )}
            <button
              onClick={refetch}
              disabled={loading}
              className="ml-auto flex items-center gap-1 text-droid-600 hover:text-droid-700 dark:text-droid-400"
              title={t('conab.refresh') || 'Atualizar'}
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* AGC: Data table */}
          {data.rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 dark:bg-white/[0.04] text-left">
                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                      {t('conab.dateCol') || 'Data'}
                    </th>
                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                      {t('conab.productCol') || 'Produto'}
                    </th>
                    {data.rows.some(r => r.uf) && (
                      <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">UF</th>
                    )}
                    {data.rows.some(r => r.municipio) && (
                      <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                        {t('conab.municipioCol') || 'Município'}
                      </th>
                    )}
                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300 text-right">
                      {t('conab.priceCol') || 'Preço'}
                    </th>
                    {data.rows.some(r => r.unit) && (
                      <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                        {t('conab.unitCol') || 'Unidade'}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t border-slate-100 dark:border-white/5 ${
                        i % 2 === 0 ? '' : 'bg-slate-50 dark:bg-white/[0.02]'
                      } hover:bg-droid-50 dark:hover:bg-white/5 transition-colors`}
                    >
                      <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {formatDateStr(row.date)}
                      </td>
                      <td className="px-3 py-1.5 text-slate-800 dark:text-slate-200 capitalize">
                        {row.product || '—'}
                      </td>
                      {data.rows.some(r => r.uf) && (
                        <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300">
                          {row.uf || '—'}
                        </td>
                      )}
                      {data.rows.some(r => r.municipio) && (
                        <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300">
                          {row.municipio || '—'}
                        </td>
                      )}
                      <td className="px-3 py-1.5 text-right font-mono text-slate-800 dark:text-slate-200">
                        {row.price !== null ? `R$ ${formatPrice(row.price)}` : '—'}
                      </td>
                      {data.rows.some(r => r.unit) && (
                        <td className="px-3 py-1.5 text-slate-500 dark:text-slate-400 text-xs">
                          {row.unit || '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('conab.noResults') || 'Nenhum resultado encontrado para os filtros selecionados.'}
            </div>
          )}
        </div>
      )}

      {/* AGC: Initial state - no search performed yet */}
      {!hasSearched && !loading && (
        <div className="text-center py-8 text-sm text-slate-400 dark:text-slate-500">
          {t('conab.initialHint') || 'Selecione os filtros e clique em "Buscar" para consultar preços CONAB.'}
        </div>
      )}
    </div>
  )
}

export default ConabPriceTable
